import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ConfirmationDialogComponent } from '@app/components';
import { Payroll } from '@app/models/payroll/payroll.model';
import { BaseForm } from '@app/modules/forms/base.form';
import { PayrollResources } from '@app/modules/payroll/payroll.resources';
import { EmployeeHourTable } from '@app/modules/payroll/services';
import { BulkSelection } from '@app/modules/table/types/bulk-selection.type';
import { AuthService, NotifyService } from '@app/services';
import { Observable, Subject, zip } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { SendHoursApprovalReminderForm } from '../send-hours-approval-reminder/send-hours-approval-reminder.form';

type Totals = {
    employees: number;
    approved: number;
    pending: number;
    new: number;
};

@Component({
    selector: 'app-payroll-hours-form[payroll]',
    templateUrl: './payroll-hours.template.html',
    styleUrls: ['./payroll-hours.style.scss'],
})
export class PayrollHoursForm extends BaseForm implements OnInit, OnDestroy {
    @Input() payroll!: Payroll;
    @Input() showForm!: boolean;
    @ViewChild('sendHoursApprovalReminderForm') sendHoursApprovalReminderForm: SendHoursApprovalReminderForm;
    @ViewChild('lockWithPendingHoursConfirmation') lockWithPendingHoursConfirmation?: ConfirmationDialogComponent;
    @ViewChild('lockWithNewHoursConfirmation') lockWithNewHoursConfirmation?: ConfirmationDialogComponent;
    @ViewChild('lockWithoutPendingHoursConfirmation') lockWithoutPendingHoursConfirmation?: ConfirmationDialogComponent;

    @Output() onLoading = new EventEmitter<boolean>();
    @Output() onUnlocked = new EventEmitter<boolean>();
    @Output() onHoursAssigned = new EventEmitter<boolean>();

    table!: EmployeeHourTable;
    tableLate!: EmployeeHourTable;
    bulkSelection?: BulkSelection;

    showSendHoursApprovalReminderModal = false;
    showUnlockWithNewHoursModal = false;

    _totalNewHours = 0;
    _prevTotalNewHours = 0;
    get totalNewHours(): number {
        return this._totalNewHours;
    }
    set totalNewHours(newHours: number) {
        this._prevTotalNewHours = this._totalNewHours;
        this._totalNewHours = newHours;
    }
    get hasNewHoursSinceRefreshing(): boolean {
        return this._totalNewHours > this._prevTotalNewHours;
    }

    totalPendingHours = 0;
    get hasPendingHours(): boolean {
        return this.totalPendingHours > 0;
    }

    hoursLocked = false;

    totals: Totals = {
        employees: 0,
        approved: 0,
        pending: 0,
        new: 0,
    };
    lateTotals: Totals = {
        employees: 0,
        approved: 0,
        pending: 0,
        new: 0,
    };

    private _onDestroy$ = new Subject();

    constructor(
        private auth: AuthService,
        private notify: NotifyService,
        private http: HttpClient
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.table = new EmployeeHourTable(this.auth, false, this.payroll);
        this.tableLate = new EmployeeHourTable(this.auth, true, this.payroll, true);
        this.subscribeToMetadata();
        this.hoursLocked = this.payroll.hoursLocked;
    }

    ngOnDestroy(): void {
        this._onDestroy$.next();
    }

    async submitHoursApprovalReminderForm(): Promise<void> {
        const hoursApprovalReminder = this.sendHoursApprovalReminderForm.hoursApprovalReminder;

        this.sendHoursApprovalReminderForm.submit();
        if (!this.sendHoursApprovalReminderForm.valid) {
            return;
        }

        hoursApprovalReminder.payPeriodStartDate = this.payroll.startDate;
        hoursApprovalReminder.payPeriodEndDate = this.payroll.endDate;

        try {
            await hoursApprovalReminder.param('company', this.auth.company?.id).save();
            this.notify.success('payroll.run.sentHoursApprovalReminder');
        } catch (err) {
            this.notify.error(err);
            throw err;
        }

        this.sendHoursApprovalReminderForm.reset();
        this.showSendHoursApprovalReminderModal = false;
    }

    /**
     * Display the confirmation modals before actually locking
     */
    async onLock(): Promise<void> {
        const lockConfirmation = this.hasPendingHours
            ? this.lockWithPendingHoursConfirmation
            : this.lockWithoutPendingHoursConfirmation;

        if (!(await lockConfirmation?.open())) return;

        if (this.hasNewHoursSinceRefreshing && !(await this.lockWithNewHoursConfirmation?.open())) {
            this.reloadTables();
            return;
        }

        try {
            this.onLoading.emit(true);
            await this.lock();

            this.hoursLocked = true;
            this.onUnlocked.emit(false);
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.onLoading.emit(false);
        }
    }

    /**
     * Unlock the hours and notify the user if new hours are available in the database
     */
    async onUnlock(): Promise<void> {
        try {
            this.onLoading.emit(true);
            await this.unlock();
            this.reloadTables();
            this.hoursLocked = false;

            if (this.hasNewHoursSinceRefreshing) {
                this.showUnlockWithNewHoursModal = true;
            }
            this.onUnlocked.emit(true);
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.onLoading.emit(false);
        }
    }

    async onRefreshHours(lateOnly: boolean = false): Promise<void> {
        try {
            this.onLoading.emit(true);

            if (lateOnly) {
                this.tableLate.reload();
            }

            this.table.reload();
        } catch (e) {
            this.notify.error(e);
        } finally {
            this.onLoading.emit(false);
        }
    }

    /**
     * Lock the hours
     */
    async lock(): Promise<void> {
        await this.http
            .put(
                PayrollResources.LockPayrollHours.replace(':company', this.auth.company.id.toString()).replace(
                    ':payroll',
                    this.payroll.id.toString()
                ),
                {}
            )
            .toPromise();
    }

    /**
     * Unlock the hours
     */
    async unlock(): Promise<void> {
        await this.http
            .put(
                PayrollResources.UnlockPayrollHours.replace(':company', this.auth.company.id.toString()).replace(
                    ':payroll',
                    this.payroll.id.toString()
                ),
                {}
            )
            .toPromise();
    }

    /**
     * Refresh the hours and disable the button
     */
    async refreshHours(): Promise<void> {
        await this.http
            .post(PayrollResources.RefreshPayrollHours, {
                payPeriodStartDate: this.payroll.startDate,
                payPeriodEndDate: this.payroll.endDate,
            })
            .toPromise();
    }

    async reloadTables(): Promise<void> {
        this.table.reload();
        this.tableLate.reload();
    }

    async onAssignHours(): Promise<void> {
        await this.http
            .post(PayrollResources.BulkAssignPayrollHours.replace(':payroll', this.payroll.id.toString()), {
                data: {
                    attributes: {
                        bulkSelection: {
                            selectedIds: Array.from(this.bulkSelection?.selectedIds || []),
                            isAllSelected: this.bulkSelection?.isAllSelected,
                        },
                    },
                },
            })
            .toPromise();

        this.onHoursAssigned.emit();
        this.reloadTables();
    }

    selectionChanged(bulkSelection: BulkSelection): void {
        this.bulkSelection = bulkSelection;
    }

    /**
     * Extract table metadata when it finishes loading
     */
    private subscribeToMetadata(): void {
        const tableMetaPipe = (table: EmployeeHourTable): Observable<Totals> =>
            table.onLoading.pipe(
                filter((loading) => !loading),
                map(() => {
                    const employees = Number(table.paginator.meta?.totalEmployees);
                    const approved = Number(table.paginator.meta?.totalApprovedHours);
                    const pending = Number(table.paginator.meta?.totalPendingHours);
                    const newHours = Number(table.paginator.meta?.totalNewHours);

                    return { employees, approved, pending, new: newHours };
                })
            );

        zip(tableMetaPipe(this.table), tableMetaPipe(this.tableLate))
            .pipe(takeUntil(this._onDestroy$))
            .subscribe(([totals, lateTotals]) => {
                this.totals = totals;
                this.lateTotals = lateTotals;

                this.totalPendingHours = totals.pending + lateTotals.pending;
                this.totalNewHours = totals.new + lateTotals.new;
            });
    }
}
